/**
 * Axios instance + token storage + refresh interceptor.
 *
 * Token storage
 * -------------
 * Access + refresh tokens live in localStorage. Not ideal vs httpOnly
 * cookies (XSS can read them) but acceptable for v1.0 — the platform
 * doesn't host user-generated content that could inject scripts, and
 * the refresh token rotates on every use (backend revokes the old one),
 * so even if a token leaks the window for misuse is small.
 *
 * Migration path: swap to httpOnly cookies + SameSite=Strict in v1.1
 * once we wire CSRF protection. The useAuth hook hides the storage
 * detail so that swap is contained.
 *
 * Refresh interceptor
 * -------------------
 * On any 401 the interceptor tries /auth/refresh exactly once. Requests
 * made during a refresh attempt queue behind a shared promise so we
 * don't stampede the refresh endpoint.
 */

import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import type { TokenPair } from "./types";

const ACCESS_KEY = "oneads.access_token";
const REFRESH_KEY = "oneads.refresh_token";

export const api = axios.create({
  baseURL: "/",
  headers: { "Content-Type": "application/json" },
});

// --- token helpers --------------------------------------------------------

export function getAccessToken(): string | null {
  return localStorage.getItem(ACCESS_KEY);
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_KEY);
}

export function saveTokens(pair: TokenPair): void {
  localStorage.setItem(ACCESS_KEY, pair.access_token);
  localStorage.setItem(REFRESH_KEY, pair.refresh_token);
}

export function clearTokens(): void {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
}

// --- request interceptor: attach Authorization ---------------------------

api.interceptors.request.use((config) => {
  const token = getAccessToken();
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// --- response interceptor: refresh on 401 --------------------------------

// Shared promise so concurrent 401s from N in-flight requests wait on
// a single refresh attempt.
let refreshInFlight: Promise<string | null> | null = null;

// How long we'll wait for /auth/refresh to respond before giving up
// and letting the user re-login. Without this cap, a hung network
// call leaves `refreshInFlight` pending forever and every subsequent
// 401 piles up behind the dead promise — the UI appears to "stop
// responding" but actually just stops retrying.
const REFRESH_TIMEOUT_MS = 10_000;

/**
 * Decode the `org_id` claim from a stored (possibly expired) access
 * token without verifying the signature. We only trust the backend to
 * validate org membership on refresh; the claim here is a hint so the
 * backend re-scopes the new access token to the org the user was
 * actively working in, instead of falling back to `users.org_id`.
 */
function getActiveOrgIdFromAccessToken(): number | null {
  const token = getAccessToken();
  if (!token) return null;
  const parts = token.split(".");
  if (parts.length < 2 || !parts[1]) return null;
  try {
    // base64url → base64 → JSON
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const payload = JSON.parse(atob(padded)) as { org_id?: unknown };
    const orgId = payload.org_id;
    return typeof orgId === "number" ? orgId : null;
  } catch {
    return null;
  }
}

async function refreshAccessToken(): Promise<string | null> {
  const startedWith = getRefreshToken();
  if (!startedWith) return null;

  const exchange = async (): Promise<string | null> => {
    // Re-read inside the lock. Every tab of this origin shares one token in
    // localStorage but has its own copy of this module, so a sibling that
    // held the lock first has already rotated it — and presenting the token
    // we captured before waiting would be a replay. The backend cannot tell
    // that from a stolen token: it ends every session and mails the owner.
    // So the whole point of the lock is this comparison; the lock alone
    // would only serialise the stampede, not prevent it.
    const current = getRefreshToken();
    if (current && current !== startedWith) {
      return getAccessToken();
    }
    try {
      const { data } = await axios.post<TokenPair>("/auth/refresh", {
        refresh_token: startedWith,
        org_id: getActiveOrgIdFromAccessToken(),
      });
      saveTokens(data);
      return data.access_token;
    } catch {
      clearTokens();
      return null;
    }
  };

  // Web Locks is the only cross-tab mutex a page gets. Where it is missing
  // we fall back to the per-tab promise, which is what we had: one tab is
  // still safe, several are not.
  const locks = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (locks?.request) {
    return locks.request("oneads.token-refresh", exchange);
  }
  return exchange();
}

/**
 * Refresh the access token, sharing the in-flight attempt with axios.
 *
 * Exported for the one request that cannot go through the interceptor: the
 * CPP picker needs `fetch` to read a stream, so it carries the token by hand
 * and a 401 there used to surface as "HTTP 401" in the modal with no retry.
 * Invisible while access tokens lived a day; at an hour it is what a tab
 * left open over lunch does. Sharing the promise matters — a stream opening
 * at the same moment as a poll must not start a second refresh and rotate
 * the token out from under the first.
 */
export async function ensureFreshAccessToken(): Promise<string | null> {
  refreshInFlight ??= Promise.race<string | null>([
    refreshAccessToken(),
    new Promise<null>((resolve) =>
      setTimeout(() => resolve(null), REFRESH_TIMEOUT_MS)
    ),
  ]).finally(() => {
    refreshInFlight = null;
  });
  return refreshInFlight;
}

type RetryConfig = AxiosRequestConfig & { _retry?: boolean };

api.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as RetryConfig | undefined;
    const status = error.response?.status;

    // Don't try to refresh on the refresh endpoint itself, on login,
    // or on a request that already retried once.
    const url = original?.url ?? "";
    const skipRefresh =
      url.includes("/auth/refresh") ||
      url.includes("/auth/login") ||
      original?._retry;

    if (status !== 401 || !original || skipRefresh) {
      return Promise.reject(error);
    }

    // Same helper the streaming path uses, so both share one attempt.
    const newToken = await ensureFreshAccessToken();
    if (!newToken) {
      // Refresh failed — bail out. AuthProvider's 401 handler will
      // redirect to /login when it notices `me` fails.
      return Promise.reject(error);
    }

    original._retry = true;
    original.headers = { ...(original.headers ?? {}), Authorization: `Bearer ${newToken}` };
    return api(original);
  }
);
